/**
 * AddTransaction2 Component
 * Clean, simple transaction entry form
 * Uses the new transaction service with proper validation
 */

import { useState, useEffect } from 'react';
import { addTransaction } from '@/services/transactionService2';
import toast from 'react-hot-toast';

const INITIAL_FORM = {
  type: 'expense',
  amount: '',
  currency: 'GBP',
  categoryId: '',
  categoryName: '',
  description: '',
  notes: '',
  date: '', // yyyy-mm-dd from <input type="date">
};

export default function AddTransaction2({ onSuccess, onCancel, initialType = 'expense' }) {
  const [form, setForm] = useState({ ...INITIAL_FORM, type: initialType });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Update type when initialType prop changes
  useEffect(() => {
    setForm(prev => ({ ...prev, type: initialType }));
  }, [initialType]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const result = await addTransaction({
        type: form.type,
        amount: form.amount,
        currency: form.currency,
        categoryId: form.categoryId || null,
        categoryName: form.categoryName || null,
        description: form.description,
        notes: form.notes,
        date: form.date || null,
      });

      toast.success(`${form.type === 'expense' ? 'Expense' : 'Income'} added successfully`);
      setForm(INITIAL_FORM);
      
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error('Add transaction error:', err);
      const errorMessage = err.message || 'Failed to add transaction.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Type */}
      <div>
        <label className="block text-sm font-medium mb-1">Type</label>
        <select
          name="type"
          value={form.type}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full bg-white"
        >
          <option value="expense">Expense</option>
          <option value="income">Income</option>
        </select>
      </div>

      {/* Amount */}
      <div>
        <label className="block text-sm font-medium mb-1">Amount *</label>
        <input
          type="number"
          min="0"
          step="0.01"
          name="amount"
          value={form.amount}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
          required
          placeholder="0.00"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Description *</label>
        <input
          type="text"
          name="description"
          value={form.description}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
          required
          placeholder="e.g. Tesco groceries, Monthly salary"
        />
      </div>

      {/* Category (simple for now) */}
      <div>
        <label className="block text-sm font-medium mb-1">Category</label>
        <input
          type="text"
          name="categoryName"
          value={form.categoryName}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
          placeholder="e.g. Groceries, Salary, Transport"
        />
      </div>

      {/* Date */}
      <div>
        <label className="block text-sm font-medium mb-1">Date</label>
        <input
          type="date"
          name="date"
          value={form.date}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium mb-1">Notes (optional)</label>
        <textarea
          name="notes"
          value={form.notes}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
          rows={2}
          placeholder="Additional details..."
        />
      </div>

      {/* Error feedback */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 px-4 py-2 rounded bg-black text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : `Add ${form.type === 'expense' ? 'Expense' : 'Income'}`}
        </button>
        
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
